/**
 * Reveal-step model — progressive ground reading from confirmed Ground.
 * pH / drainage / zone / frost are live. Horizon inches stay a texture sketch.
 */

export const REVEAL_STAGE_MAX = 4;
export const REVEAL_STAGE_DELAYS_MS = Object.freeze([400, 1100, 1800, 2500]);

export function dialAngleDeg(t) {
  const clamped = Math.max(0, Math.min(1, Number(t) || 0));
  return -120 + clamped * 240;
}

/** Map USDA zone (e.g. 8a) onto the gauge arc. Null if unparseable. */
export function zoneDialT(zone) {
  const match = /^(\d{1,2})([ab])?$/i.exec(String(zone || '').trim());
  if (!match) return null;
  const n = Number(match[1]);
  if (!Number.isFinite(n) || n < 1 || n > 13) return null;
  const half = match[2] && match[2].toLowerCase() === 'b' ? 0.5 : 0;
  return Math.max(0, Math.min(1, (n + half - 1) / 12));
}

function soilTextureFamily(place, draftSoilTypeId = '') {
  const blob = `${draftSoilTypeId || ''} ${place?.soilTypeId || ''} ${place?.series || ''}`.toLowerCase();
  if (/\bsand/.test(blob) && !/clay/.test(blob)) return 'sand';
  if (/loam/.test(blob) && !/clay/.test(blob)) return 'loam';
  if (/clay|muck/.test(blob)) return 'clay';
  return 'unknown';
}

function buildSoilBands(family, stage) {
  const rev = Math.max(0, Math.min(REVEAL_STAGE_MAX, Number(stage) || 0));
  const midLabel = family === 'sand'
    ? 'SANDY SOIL'
    : family === 'loam'
      ? 'LOAM'
      : family === 'clay'
        ? 'CLAY'
        : 'SOIL';
  const midColor = family === 'sand'
    ? '#C4B48A'
    : family === 'loam'
      ? '#8B7355'
      : family === 'clay'
        ? '#8B5E3C'
        : '#6B6350';
  return [
    {
      label: rev >= 1 ? 'SURFACE' : '',
      heightPct: rev >= 1 ? 12 : 0,
      color: '#6B6350',
      fg: '#efe7d2',
    },
    {
      label: rev >= 1 ? midLabel : '',
      heightPct: rev >= 1 ? 38 : 0,
      color: midColor,
      fg: '#efe7d2',
    },
    {
      label: rev >= 2 ? 'SUBSOIL' : '',
      heightPct: rev >= 2 ? 32 : 0,
      color: '#4A4237',
      fg: '#efe7d2',
    },
    {
      label: rev >= 2 ? 'PARENT MATERIAL' : '',
      heightPct: rev >= 2 ? 18 : 0,
      color: '#8B8367',
      fg: '#221e18',
    },
  ];
}

/**
 * @param {object} opts
 * @param {object|null} opts.place - confirmed Ground (buildConfirmedGroundPlace)
 * @param {string|number} [opts.draftSoilPh]
 * @param {string} [opts.draftSoilDrainageLabel]
 * @param {string} [opts.draftSoilTypeId]
 * @param {number} opts.stage - 0..4
 */
export function buildRevealModel({
  place = null,
  draftSoilPh = '',
  draftSoilDrainageLabel = '',
  draftSoilTypeId = '',
  stage = 0,
} = {}) {
  const rev = Math.max(0, Math.min(REVEAL_STAGE_MAX, Number(stage) || 0));
  const phRaw = draftSoilPh !== '' && draftSoilPh != null
    ? parseFloat(draftSoilPh)
    : place?.ph;
  const ph = Number.isFinite(phRaw) ? phRaw : null;
  const drainLabel = String(draftSoilDrainageLabel || place?.drain || '').trim();
  const drainKnown = Boolean(drainLabel);
  const poorly = drainKnown && /poor/i.test(drainLabel);
  const series = String(place?.series || '').trim().toUpperCase() || 'NOT SURVEYED';
  const zone = String(place?.zone || '').trim();
  const firstFrost = String(place?.first || '').trim();
  const family = soilTextureFamily(place, draftSoilTypeId);

  const zoneT = zoneDialT(zone);
  const dials = [
    {
      label: 'SOIL pH',
      value: rev >= 1 ? (ph != null ? ph.toFixed(1) : '—') : '',
      deg: rev >= 1 && ph != null ? dialAngleDeg((ph - 4.5) / 3) : -120,
      verdict: rev < 1
        ? 'READING…'
        : (ph == null ? 'NOT CONFIRMED' : (ph < 5.6 ? 'NEEDS LIME' : 'WORKABLE')),
    },
    {
      label: 'DRAINAGE',
      value: rev >= 2 ? (drainKnown ? (poorly ? 'SLOW' : 'FAIR') : '—') : '',
      deg: rev >= 2 && drainKnown ? dialAngleDeg(poorly ? 0.2 : 0.55) : -120,
      verdict: rev < 2
        ? 'READING…'
        : (drainKnown ? (poorly ? 'HOLDS WATER' : 'MOVES WATER') : 'NOT CONFIRMED'),
    },
    {
      label: 'USDA ZONE',
      value: rev >= 3 ? (zone ? zone.toUpperCase() : '—') : '',
      deg: rev >= 3 && zoneT != null ? dialAngleDeg(zoneT) : -120,
      verdict: rev < 3
        ? 'READING…'
        : (zone ? 'CONFIRMED' : 'NOT CONFIRMED'),
    },
  ];

  const soilBands = buildSoilBands(family, rev);

  let phLine = 'pH is not confirmed on Ground yet. Lime and fertilizer rates wait on a reading.';
  if (ph != null && ph < 5.6) {
    phLine = `At pH ${ph.toFixed(1)} your ground locks up phosphorus. Clover will not fix nitrogen properly below 6.0 — lime is doing more for you here than fertilizer.`;
  } else if (ph != null) {
    phLine = `pH ${ph.toFixed(1)} is in workable range. Starter fertilizer will actually be taken up.`;
  }

  let drainLine = 'Drainage is not confirmed on Ground yet.';
  if (drainKnown && poorly) {
    drainLine = `${drainLabel} ground holds water. Small seed broadcast on the surface will rot in a wet week, so plant into a worked seedbed and stay off the low end of the plot.`;
  } else if (drainKnown) {
    drainLine = `${drainLabel} drainage. Still work a seedbed where you can — surface broadcast is less reliable after a wet week.`;
  }

  let zoneLine = 'USDA zone is not confirmed on Ground yet.';
  if (zone) {
    zoneLine = `USDA zone ${zone} is confirmed for this site. Timing uses this zone’s frost window.`;
  }

  let frostLine = 'USDA zone and frost are not confirmed on Ground yet.';
  if (zone && firstFrost) {
    frostLine = `Zone ${zone} gives you until roughly ${firstFrost} before first frost. That is enough runway for a fall grain to establish if seed goes down by mid-October.`;
  } else if (zone) {
    frostLine = `Zone ${zone} is set. Add last/first frost on Ground so the fall window is a real date, not a guess.`;
  } else if (firstFrost) {
    frostLine = `First frost is ${firstFrost}. Set USDA zone on Ground so the rest of the plan matches this site.`;
  }

  let crossNote = 'Soil survey has not filled a profile yet. This stack is a sketch, not a core sample.';
  if (family !== 'unknown' || drainKnown) {
    const textureBit = family === 'unknown' ? 'this soil' : midLabelForFamily(family).toLowerCase();
    crossNote = drainKnown
      ? `Survey texture is ${textureBit}. Drainage is ${drainLabel.toLowerCase()}. Horizon depths and water-table inches are not in this layer — do not read the stack as a measured core.`
      : `Survey texture is ${textureBit}. Horizon depths and water-table inches are not in this layer — do not read the stack as a measured core.`;
  }

  const lines = [
    { text: phLine, dot: '#b4501c', alt: false, on: rev >= 1 },
    { text: drainLine, dot: '#4e7ea8', alt: true, on: rev >= 2 },
    { text: zoneLine, dot: '#c08a20', alt: false, on: rev >= 3 },
    { text: frostLine, dot: '#3b4e3a', alt: true, on: rev >= 4 },
  ];

  return {
    stage: rev,
    done: rev >= REVEAL_STAGE_MAX,
    status: rev >= REVEAL_STAGE_MAX ? 'DONE' : 'READING…',
    soilSeries: series,
    dials,
    soilBands,
    lines,
    crossNoteVisible: rev >= 2,
    crossNote,
  };
}

function midLabelForFamily(family) {
  if (family === 'sand') return 'SANDY SOIL';
  if (family === 'loam') return 'LOAM';
  if (family === 'clay') return 'CLAY';
  return 'SOIL';
}

export function revealContinueLabel(stage) {
  const rev = Math.max(0, Math.min(REVEAL_STAGE_MAX, Number(stage) || 0));
  if (rev < REVEAL_STAGE_MAX) return 'READING THE GROUND…';
  return 'SEE THE PLAN';
}
